import { useCallback, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { GLOBALTYPES } from "../redux/actions/globalTypes";
import { getDataAPI, postDataAPI } from "../utils/fetchData";

export default function Addfreecourse(props) {
	const { profile, auth } = useSelector((state) => state);
	const dispatch = useDispatch();

	// Modal states
	const [showFreeUploadModal, setShowFreeUploadModal] = useState(false);
	const [organisationModal, setOrganisationModal] = useState(false);

	// Form states
	const [formData, setFormData] = useState({
		courseTitle: "",
		subject: "",
		sourceLink: "",
		selectedOrganisation: "",
		selectedLanguage: "",
	});

	// Data states
	const [organisationArray, setOrganisationArray] = useState([]);
	const [organisationName, setOrganisationName] = useState("");
	const [languages, setLanguages] = useState([]);

	// Loading states
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoadingOrganisations, setIsLoadingOrganisations] = useState(false);

	// Validation states
	const [errors, setErrors] = useState({});

	// Fetch organisations on component mount
	useEffect(() => {
		fetchOrganisations();
	}, []);

	// Update languages when props change
	useEffect(() => {
		if (props.languages) {
			setLanguages(props.languages);
		}
	}, [props.languages]);

	// Fetch organisations with error handling
	const fetchOrganisations = useCallback(async () => {
		try {
			setIsLoadingOrganisations(true);
			const res = await getDataAPI(`getcourseorganisation`, auth.token);
			console.log("res getcourseorganisations", res.data);
			setOrganisationArray(res.data.organisations || []);
		} catch (err) {
			if (!err.isCanceled) {
				console.error("Failed to fetch organisations:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: "Failed to load organisations. Please try again." },
				});
			}
		} finally {
			setIsLoadingOrganisations(false);
		}
	}, [auth.token, dispatch]);

	// Form validation
	const validateForm = () => {
		const newErrors = {};

		if (!formData.courseTitle.trim()) {
			newErrors.courseTitle = "Course title is required";
		}

		if (!formData.subject.trim()) {
			newErrors.subject = "Subject is required";
		}

		if (!formData.sourceLink.trim()) {
			newErrors.sourceLink = "Source link is required";
		} else if (!isValidUrl(formData.sourceLink)) {
			newErrors.sourceLink = "Please enter a valid URL";
		}

		if (!formData.selectedOrganisation) {
			newErrors.selectedOrganisation = "Please select an organisation";
		}

		if (!formData.selectedLanguage) {
			newErrors.selectedLanguage = "Please select a language";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// URL validation helper
	const isValidUrl = (string) => {
		try {
			new URL(string);
			return true;
		} catch (_) {
			return false;
		}
	};

	// Handle form field changes
	const handleInputChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		// Clear error when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({
				...prev,
				[field]: "",
			}));
		}
	};

	// Handle course upload
	const handlePushItemsIntoArray = async () => {
		if (!validateForm()) {
			return;
		}

		try {
			setIsSubmitting(true);
			dispatch({
				type: GLOBALTYPES.ALERT,
				payload: { loading: true },
			});

			const formDataToSend = new FormData();
			const courseData = {
				languageid: formData.selectedLanguage,
				courseTitle: formData.courseTitle.trim(),
				subject: formData.subject.trim(),
				sourceLink: formData.sourceLink.trim(),
				organisation: formData.selectedOrganisation,
			};

			Object.keys(courseData).forEach((key) => {
				formDataToSend.append(key, courseData[key]);
			});

			const res = await postDataAPI(
				"createfreecourse",
				formDataToSend,
				auth.token
			);
			console.log("res", res);

			if (res.status === 200) {
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { success: "Free course uploaded successfully!" },
				});

				// Reset form
				setFormData({
					courseTitle: "",
					subject: "",
					sourceLink: "",
					selectedOrganisation: "",
					selectedLanguage: "",
				});
				setErrors({});
				setShowFreeUploadModal(false);

				// Reload page after success
				setTimeout(() => {
					window.location.reload();
				}, 1500);
			}
		} catch (err) {
			if (!err.isCanceled) {
				const errorMsg =
					err.response?.data?.msg ||
					"Failed to upload course. Please try again.";
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: errorMsg },
				});
			}
		} finally {
			setIsSubmitting(false);
			dispatch({
				type: GLOBALTYPES.ALERT,
				payload: { loading: false },
			});
		}
	};

	// Handle organisation creation
	const handlePushItemIntoFreeOrganisationArray = async () => {
		if (!organisationName.trim()) {
			dispatch({
				type: GLOBALTYPES.ALERT,
				payload: { error: "Organisation name is required" },
			});
			return;
		}

		try {
			setIsSubmitting(true);

			const res = await postDataAPI(
				"createcourseorganisation",
				{
					name: organisationName.trim(),
				},
				auth.token
			);
			console.log("res", res);

			if (res.status === 200) {
				setOrganisationArray((prev) => [...prev, res.data.organisation]);
				setOrganisationName("");
				setOrganisationModal(false);

				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { success: "Organisation created successfully!" },
				});
			}
		} catch (err) {
			if (!err.isCanceled) {
				const errorMsg =
					err.response?.data?.msg ||
					"Failed to create organisation. Please try again.";
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: errorMsg },
				});
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	// Close modals and reset form
	const handleCloseModal = (modalType) => {
		if (modalType === "course") {
			setShowFreeUploadModal(false);
			setFormData({
				courseTitle: "",
				subject: "",
				sourceLink: "",
				selectedOrganisation: "",
				selectedLanguage: "",
			});
			setErrors({});
		} else if (modalType === "organisation") {
			setOrganisationModal(false);
			setOrganisationName("");
		}
	};

	// Error display component
	const ErrorMessage = ({ error }) => {
		if (!error) return null;
		return (
			<div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>
				{error}
			</div>
		);
	};

	return (
		<>
			{/* Organisation Modal */}
			<Modal
				show={organisationModal}
				onHide={() => handleCloseModal("organisation")}
				dialogClassName="modal-sm-30px"
				size="md"
				backdropClassName="childmodal"
				centered>
				<Modal.Body style={{ padding: "0px 12px 0px 12px" }}>
					<div className="row" style={{ background: "#F4F7F9" }}>
						<div className="col-11">
							<h2
								style={{
									fontSize: "18px",
									fontWeight: "500",
									padding: "15px",
								}}>
								Add Organisation
							</h2>
						</div>
						<div
							className="col-1"
							style={{ padding: "15px", cursor: "pointer" }}
							onClick={() => handleCloseModal("organisation")}>
							<img alt="close" src="../images/closeOne.png" />
						</div>
					</div>
					<div>
						<div style={{ padding: "15px 0", paddingLeft: "10px" }}>
							<div style={{ fontSize: "13px" }}>Course Organisation Name:</div>
							<input
								type="text"
								className="form-control"
								placeholder="Enter Course Organisation name"
								value={organisationName}
								onChange={(e) => setOrganisationName(e.target.value)}
								disabled={isSubmitting}
							/>
						</div>
					</div>
					<div style={{ textAlign: "end", paddingBottom: "10px" }}>
						<Button
							style={{ background: "#20ce8f", border: "none" }}
							disabled={!organisationName.trim() || isSubmitting}
							onClick={handlePushItemIntoFreeOrganisationArray}>
							{isSubmitting ? "Saving..." : "Save"}
						</Button>
					</div>
				</Modal.Body>
			</Modal>

			{/* Course Upload Modal */}
			<Modal
				show={showFreeUploadModal}
				onHide={() => handleCloseModal("course")}
				dialogClassName="modal-sm-30px"
				size="md"
				backdropClassName="childmodal"
				centered>
				<Modal.Body style={{ padding: "0px 12px 0px 12px" }}>
					<div className="row" style={{ background: "#F4F7F9" }}>
						<div className="col-11">
							<h2
								style={{
									fontSize: "18px",
									fontWeight: "500",
									padding: "15px",
								}}>
								Upload Free Course
							</h2>
						</div>
						<div
							className="col-1"
							style={{ padding: "15px", cursor: "pointer", textAlign: "right" }}
							onClick={() => handleCloseModal("course")}>
							<img alt="close" src="../images/closeOne.png" />
						</div>
					</div>

					<div className="row">
						<div className="col-12">
							<div style={{ paddingLeft: "10px" }}>
								<div style={{ fontSize: "13px" }}>Organisation:</div>
								<select
									name="category"
									value={formData.selectedOrganisation}
									onChange={(e) =>
										handleInputChange("selectedOrganisation", e.target.value)
									}
									disabled={isSubmitting || isLoadingOrganisations}
									className={
										errors.selectedOrganisation
											? "form-control is-invalid"
											: "form-control"
									}>
									<option value="">Select</option>
									{organisationArray.map((org) => (
										<option value={org.id} key={org.id}>
											{org.name}
										</option>
									))}
								</select>
								<ErrorMessage error={errors.selectedOrganisation} />
								<span style={{ marginLeft: "30px" }}>
									<Button
										onClick={() => setOrganisationModal(true)}
										disabled={isSubmitting}
										style={{ background: "#20ce8f", border: "none" }}>
										Add
									</Button>
								</span>
							</div>
						</div>
					</div>

					<div style={{ paddingLeft: "10px", paddingBottom: "10px" }}>
						<div style={{ fontSize: "13px" }}>Language:</div>
						<select
							name="language"
							value={formData.selectedLanguage}
							onChange={(e) =>
								handleInputChange("selectedLanguage", e.target.value)
							}
							disabled={isSubmitting}
							className={
								errors.selectedLanguage
									? "form-control is-invalid"
									: "form-control"
							}>
							<option value="">Select</option>
							{languages.map((lang) => (
								<option value={lang.id} key={lang.id}>
									{lang.name}
								</option>
							))}
						</select>
						<ErrorMessage error={errors.selectedLanguage} />
						<span style={{ marginLeft: "30px" }}>
							<Button
								onClick={() => props.showLanguageModel()}
								disabled={isSubmitting}
								style={{ background: "#20ce8f", border: "none" }}>
								Add
							</Button>
						</span>
					</div>

					<div style={{ paddingTop: "15px", paddingLeft: "10px" }}>
						<div style={{ fontSize: "13px" }}>Course title:</div>
						<input
							type="text"
							className={
								errors.courseTitle ? "form-control is-invalid" : "form-control"
							}
							placeholder="Enter Course Title name"
							value={formData.courseTitle}
							onChange={(e) => handleInputChange("courseTitle", e.target.value)}
							disabled={isSubmitting}
						/>
						<ErrorMessage error={errors.courseTitle} />
					</div>

					<div style={{ paddingTop: "15px", paddingLeft: "10px" }}>
						<div style={{ fontSize: "13px" }}>Subject:</div>
						<input
							type="text"
							className={
								errors.subject ? "form-control is-invalid" : "form-control"
							}
							placeholder="Enter Subject"
							value={formData.subject}
							onChange={(e) => handleInputChange("subject", e.target.value)}
							disabled={isSubmitting}
						/>
						<ErrorMessage error={errors.subject} />
					</div>

					<div style={{ paddingTop: "15px", paddingLeft: "10px" }}>
						<div style={{ fontSize: "13px" }}>Source link:</div>
						<input
							type="url"
							className={
								errors.sourceLink ? "form-control is-invalid" : "form-control"
							}
							placeholder="Enter Source link (e.g., https://example.com)"
							value={formData.sourceLink}
							onChange={(e) => handleInputChange("sourceLink", e.target.value)}
							disabled={isSubmitting}
						/>
						<ErrorMessage error={errors.sourceLink} />
					</div>

					<div
						style={{
							textAlign: "center",
							paddingBottom: "10px",
							paddingTop: "20px",
						}}>
						<Button
							style={{ background: "#20ce8f", border: "none" }}
							disabled={isSubmitting}
							onClick={handlePushItemsIntoArray}>
							{isSubmitting ? "Uploading..." : "Upload"}
						</Button>
					</div>
				</Modal.Body>
			</Modal>

			<Button
				className="mb-c"
				style={{
					background: "#36BB7D",
					fontSize: "13px",
					borderRadius: "4px",
					border: "none",
					marginRight: "5px",
				}}
				onClick={() => setShowFreeUploadModal(true)}>
				+ Add Free Course
			</Button>
		</>
	);
}
